import React from "react";
import { useFormContext } from "react-hook-form";
import { Info } from "lucide-react";

import { MultiSelectField } from "components/AddVaccineForm/components";
import { Checkbox } from "components/ui/Checkbox";

import style from "../AddVaccineForm.module.css";
import { ImmunocompromisedField } from "./ImmunocompromisedField";

// Additional factors and immunocompromised statuses arrive with the risk factors and
// are versioned with them: spec 4.3 offers X0064-1..11 and the four X0068 IC states,
// 4.8 replaces both sets outright (X0064-14..17, and IC reduced to Yes/No). Hardcoding
// either list pinned the form to one spec while the server moved on, so the codes are
// taken from the response and a list the server does not send is not shown at all.
const toFieldOptions = (factors) =>
  (factors || []).map(({ code, description }) => ({
    value: code,
    label: description,
  }));

const RiskItem = ({ code, description, disabled, checked, register }) => (
  <div
    className={`${style.riskItem} ${disabled ? style.riskItemDisabled : ""}`}
  >
    <Checkbox
      id={`risk-${code}`}
      value={code}
      checked={disabled ? !!checked : undefined}
      disabled={disabled}
      {...(!disabled && register ? register("riskFactorCodes") : {})}
    />
    <label className={style.riskLabelContent} htmlFor={`risk-${code}`}>
      <span className={style.riskText}>{description}</span>
    </label>
  </div>
);

const RiskFactorsList = ({ riskFactors, register }) => {
  const hasNoRisks =
    !riskFactors.defaultedRisks.length && !riskFactors.selectableRisks.length;

  if (hasNoRisks) {
    return (
      <div className={style.emptyState}>
        No specific risk factors available for this vaccine type.
      </div>
    );
  }

  return (
    <>
      {/*
       * Says where the locked risks come from, and that the date decides them (KIN-1965).
       *
       * Without this the screen shows a tick that was there, is now gone, and cannot be put back —
       * which reads as a broken control and was reported as one. The derivation was right: the
       * patient was not pregnant on the date of the dose, and a record states what was true when
       * the dose was given. `vaccinationDate` is in the query key, so moving the date re-derives
       * these; the behaviour is correct and only ever looked wrong because nothing explained it.
       */}
      {!!riskFactors.defaultedRisks.length && (
        <p className={style.derivedHint}>
          <Info size={14} className={style.derivedHintIcon} aria-hidden="true" />
          <span>
            Taken from the patient&apos;s record as at the date given, so these
            change if that date changes.
          </span>
        </p>
      )}
      {riskFactors.defaultedRisks.map((risk) => (
        <RiskItem
          key={risk.code}
          code={risk.code}
          description={risk.description}
          disabled={risk.disabled}
          checked={risk.selected}
        />
      ))}
      {riskFactors.selectableRisks.map((risk) => (
        <RiskItem
          key={risk.code}
          code={risk.code}
          description={risk.description}
          register={register}
        />
      ))}
    </>
  );
};

export const RiskFactorsSection = ({
  riskFactors,
  isEditMode,
  isLoading,
  selectedCount,
  totalAvailable,
  onClear,
  existingRiskCodes,
  isIndicationRequired,
}) => {
  const { register, watch } = useFormContext();
  // The version that will actually be submitted, not simply the one the endpoint just
  // returned: on an edit the form holds the version the record was first reported
  // under, and that is the one the ticked boxes belong to.
  const specVersion = watch("specVersion") || riskFactors?.specVersion;
  const additionalFactorOptions = toFieldOptions(riskFactors?.additionalFactors);
  const immunocompromisedOptions = toFieldOptions(
    riskFactors?.immunocompromisedStatuses
  );

  return (
    <>
      <hr className={style.sectionDivider} />
      <h6 className={style.sectionHeader}>Risk factors</h6>

      {/* Phase 2 §5.3.2. Said here rather than enforced on submit: the backend raises it
          as a question the clinician can answer and continue past, and turning it into a
          client-side block would take that decision away from them. */}
      {!!isIndicationRequired && !selectedCount && (
        <p className={style.riskRequiredHint}>
          Please select at least One Indication for the vaccination
        </p>
      )}

      <div className={style.riskSection}>
        <div className={style.riskContainer}>
          <div className={style.riskHeader}>
            <span className={style.riskTitle}>Medical Risk Factors</span>
            <div className={style.riskHeaderRight}>
              {selectedCount > 0 && (
                <>
                  <span className={style.riskSelectedCount}>
                    {selectedCount} items selected
                  </span>
                  {!isEditMode && (
                    <>
                      <button
                        type="button"
                        className={style.riskClearButton}
                        onClick={onClear}
                      >
                        Clear
                      </button>
                      <div className={style.riskDivider} />
                    </>
                  )}
                </>
              )}
              {!isEditMode && (
                <span className={style.riskCount}>
                  {totalAvailable} Available
                </span>
              )}
              {!!specVersion && (
                <>
                  {!isEditMode && <div className={style.riskDivider} />}
                  <span className={style.riskSpecVersion}>
                    Spec {specVersion}
                  </span>
                </>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className={style.loadingState}>
              <div className={style.loadingSpinner} />
              Checking patient risks...
            </div>
          ) : (
            <div className={style.riskList}>
              <RiskFactorsList
                riskFactors={riskFactors}
                isEditMode={isEditMode}
                existingRiskCodes={existingRiskCodes}
                register={register}
              />
            </div>
          )}
        </div>
      </div>

      {(!!additionalFactorOptions.length ||
        !!immunocompromisedOptions.length) && (
        <div className={style.inputRowTwo}>
          {!!additionalFactorOptions.length && (
            <MultiSelectField
              name="additionalFactors"
              label="Additional Factors"
              options={additionalFactorOptions}
              placeholder="Select"
            />
          )}
          {!!immunocompromisedOptions.length && (
            <ImmunocompromisedField options={immunocompromisedOptions} />
          )}
        </div>
      )}

    </>
  );
};
